import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { isEmpty } from 'lodash'

import { Toggle } from '@/components/forms/Checkbox'
import {
  fetchPlaybooks,
  getPlaybookPhraseSuggestions,
  fetchMultipleOrganizationsPlaybooks,
} from '@/reducers/playbooks/playbooks.actions'
import {
  massPlaybookUpdateReset,
  setIsMassPlaybookUpdateOnOff,
  setMassPlaybookUpdateMatches,
} from '@/reducers/playbooks/playbooks.redux'
import { OrganizationsFilter } from '@/components/filters/OrganizationsFilter'
import { FormField } from '@/components/forms/FormField'

import { CheckboxWrap } from '../checklist/ChecklistForm'
import {
  accessors,
  getPlaybookOrganizationNames,
  matchUpdateProgress,
} from '../../playbook.helpers'

const BulkEditToggle = ({ accessor, readOnly, isUpdate }) => {
  const dispatch = useDispatch()
  const { id: config_id, entryId } = useParams()

  const { playbook } = useSelector((state) => state.playbook)
  const { loading, massPlaybookUpdate, playbooks } = useSelector((state) => state.playbooks)
  const { userOrgHierarchy, flattenedUserOrgHierarchy } = useSelector((state) => state.orgHierarchy)
  const { organizationid: organizationId, hierarchy_manager: isHierarchyManager } = useSelector(
    (state) => state.currentUser
  )

  const { isEnabled, fetchedEntity } = massPlaybookUpdate || {}
  const isBaltoAdmin = organizationId === 1
  const organizationIds = flattenedUserOrgHierarchy.map((org) => org.organization_id)
  const canSeeOrgHierarchyFilter = !isBaltoAdmin && isHierarchyManager && organizationIds.length > 1

  const setIsMassPlaybookUpdateOn = (value) => dispatch(setIsMassPlaybookUpdateOnOff(value))

  const currentEntity = `${config_id}_${entryId}`

  function getBodyByAccessor(playbookObj, accessor) {
    if (accessor === accessors.DYNAMIC_PROMPT) {
      return { deck: playbookObj.body?.deck }
    }
    if (accessor === accessors.POSTCALL) {
      return { classified_postcall: playbookObj.body?.classified_postcall }
    }
    return { checklist: playbookObj.body?.checklist }
  }

  const fetchPlaybookPhraseSuggestions = () => {
    const configs = {}
    playbooks
      .filter((p) => {
        return !isEmpty(p.body)
      })
      .forEach((p) => {
        configs[p.id] = {
          name: p.name,
          body: getBodyByAccessor(p, accessor),
        }
      })
    // If editing a draft, include the current playbook in the list of playbooks to compare
    if (!playbook.active) {
      configs[playbook.id] = {
        name: playbook.name,
        body: getBodyByAccessor(playbook, accessor),
      }
    }
    const target_item = { config_id, uuid: entryId }
    const payload = { data: { ndarray: [{ configs, target_item }] } }

    const playbookOrganizations = isHierarchyManager
      ? getPlaybookOrganizationNames(playbooks, flattenedUserOrgHierarchy)
      : playbooks
    dispatch(getPlaybookPhraseSuggestions(payload, currentEntity, true, playbookOrganizations))
  }

  useEffect(() => {
    dispatch(massPlaybookUpdateReset())
    return () => {
      dispatch(massPlaybookUpdateReset())
    }
  }, [])

  // Preload all organization playbooks (for Mass Playbook Add & Update)
  useEffect(() => {
    if (canSeeOrgHierarchyFilter) {
      dispatch(fetchMultipleOrganizationsPlaybooks(organizationIds, true, true, true))
    } else {
      dispatch(fetchPlaybooks(playbook.organization_id, true, true, true))
    }
  }, [playbook.organization_id])

  useEffect(() => {
    if (isUpdate && !isEmpty(playbooks) && fetchedEntity !== currentEntity && !loading) {
      fetchPlaybookPhraseSuggestions()
    }
  }, [isEnabled, isUpdate, playbooks, entryId, loading])

  useEffect(() => {
    if (!isUpdate && !isEmpty(playbooks)) {
      const playbookOrganizations = isHierarchyManager
        ? getPlaybookOrganizationNames(playbooks, flattenedUserOrgHierarchy)
        : playbooks

      const otherPlaybooks = playbooks
        .filter((playbook) => playbook.id.toString() !== config_id)
        .map((playbook) => {
          const organizationInfo = playbookOrganizations[playbook.id]
          return {
            config_id: playbook.id.toString(),
            config_name: playbook.name,
            compoundId: `${playbook.id}_create`,
            progress: matchUpdateProgress.INITIAL,
            configIdOriginal: playbook.id.toString(),
            organizationId: organizationInfo ? organizationInfo.id : '',
            organizationName: organizationInfo ? organizationInfo.name : '',
          }
        })
      dispatch(setMassPlaybookUpdateMatches(otherPlaybooks))
    }
  }, [isUpdate, playbooks])

  const label = isUpdate ? 'Bulk Edit Playbooks' : 'Bulk Add to Playbooks'
  const text = isUpdate
    ? 'Let the system identify similar content across your Playbooks, allowing you to select and edit multiple items simultaneously.'
    : `Quickly add this item to other playbooks.`

  return (
    <>
      <CheckboxWrap className="playbook-config-container" label={label} text={text}>
        <div>
          <Toggle
            aria-label={label}
            checked={isEnabled}
            onChange={() => setIsMassPlaybookUpdateOn(!isEnabled)}
            disabled={readOnly}
            data-testid="mass-playbook-update-checkbox"
          />
        </div>
      </CheckboxWrap>
      {isEnabled && canSeeOrgHierarchyFilter && (
        <FormField>
          <label>Also include playbooks from</label>
          <OrganizationsFilter
            options={userOrgHierarchy}
            initialSelection={organizationIds}
            disabledIDs={[organizationId]}
          />
        </FormField>
      )}
    </>
  )
}

export default BulkEditToggle
